/**
 * accountInvitesList.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";
import QRCode from "qr-code-styling";
import React from "react";
import { Link } from "react-router-dom";

import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTimeValidationError } from "@mui/x-date-pickers/models";
//import InputLabel from "@mui/material/InputLabel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MenuIcon from "@mui/icons-material/Menu";
import InfoIcon from "@mui/icons-material/Info";

import { Alert, Autocomplete, Button, Checkbox, CircularProgress,
  FormControl, FormControlLabel, IconButton, TextField, Tooltip
 } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { AccountNavContext } from "./account.context";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { AccountInvite, InviteType, MemberType } from "../models/auth";
import { Group } from "../models/group";
import { InviteRequest, InviteResponse } from "../models/requests";

import { authServiceFactory } from "../services/auth.service";
import { groupServiceFactory } from "../services/group.service";

interface InviteWithURL extends AccountInvite {
  tokenUrl?: string
}

export function AccountInvitesListContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);

  return <div className="account-invite-list-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small" onClick={acctContext.toggleLeftNav}>
        <MenuIcon />
      </IconButton>
      <h3>Invites: {authContext.membership?.accountname || ""}</h3>
      <span className="right-controls">
        <Button size="small" component={Link} to="/admin/invite">
          <i className="fas fa-plus"></i>
          <span>&nbsp;Invite</span>
        </Button>
      </span>
    </header>

    <AccountInvitesListComponent accountID={authContext.membership?.accountID || ""}
      displayTZ={authContext.tz()}
      domain={authContext.domain} 
      compMemberships={acctContext.currentAccount?.remainingFreeMemberships}/>
  </div>;
}

interface Props {
  accountID: string,
  displayTZ: string,
  domain: string,
  compMemberships?: number,
}

export function AccountInvitesListComponent(props: Props): React.ReactElement<any, any> {
  const [errMsg, setErrMsg] = React.useState("");
  const [invites, setInvites] = React.useState<Array<InviteWithURL>>([]);
  const [detailSet, setDetailSet] = React.useState<Set<number>>(new Set());

  React.useEffect(function () {
    const authService = authServiceFactory();
    authService.getAccountInvites(props.accountID).then(setInvites, err => {
      console.error("Error getting account invites:", err);
      setErrMsg(err.message);
    });
  }, [props.accountID]);

  const now = DateTime.now();

  async function resendInvite(req: InviteRequest, index: number): Promise<InviteResponse> {
    const invite = invites[index];
    const tokenData = await authServiceFactory().sendInvite(req);

    invite.validAfter = req.validAfter || invite.validAfter;
    invite.validBefore = req.validBefore || DateTime.now().plus({days: 7});
    invite.groupIDs = req.groupIDs;
    invite.tokenUrl = tokenData.url;
    invite.compIfPossible = req.compIfPossible;

    const newArr = Array.from(invites);
    newArr[index] = invite;
    setInvites(newArr);

    return tokenData;
  }

  async function deleteInvite(index: number): Promise<any> {
    const invite = invites[index];
    await authServiceFactory().deleteInvite(invite.accountID, invite._id);

    const newArr = Array.from(invites);
    newArr.splice(index, 1);
    setInvites(newArr);
    if (detailSet.has(index)) {
      const newDetail = new Set(detailSet);
      newDetail.delete(index);
      setDetailSet(newDetail);
    }
  }

  async function getGroup(groupID: string): Promise<Group | null> {
    try {
      return await groupServiceFactory().getGroup(props.accountID, groupID);
    } catch (err) {
      console.error("Error fetching group:", err);
      setErrMsg((err as Error).message);
      return null;
    }
  }
  async function getGroups(groupIDs: Array<string>): Promise<Array<Group>> {
    try {
      return await groupServiceFactory().getGroups(props.accountID, groupIDs);
    } catch (err) {
      console.error("Error fetching groups:", err);
      setErrMsg((err as Error).message);
      return [];
    }
  }
  async function searchGroups(search: string): Promise<Array<Group>> {
    try {
      return await groupServiceFactory().searchGroups(props.accountID, search, {limit: 100, withPrivate: true});
    } catch (err) {
      console.error("Error searching groups:", err);
      setErrMsg((err as Error).message);  
      return [];
    }
  }

  function showDetailAtIndex(index: number) {
    const newDetail = new Set([...detailSet, index]);
    setDetailSet(newDetail);
  }
  function finishDetailAtIndex(index: number) {
    const newDetail = new Set(detailSet);
    newDetail.delete(index);
    setDetailSet(newDetail);
  }

  // function renderCompInviteInfo() {
  //   if (!props.compMemberships) { return <></>; }
  //   return <>
  //     <div className="account-info-inner-container">
  //       <Alert severity="info" className="free-membership-info">
  //         <p className="">You currently have {props.compMemberships} [free/complementary] 
  //           membership{props.compMemberships > 1 ? "s" : ""} that can be claimed by new users.</p>
  //         <p>[You/This Account/The account holder/etc] will not be charged for [free/complementary] memberships</p>
  //       </Alert>
  //     </div>
  //   </>
  // }

  return <div>
    {/* {renderCompInviteInfo()} */}
    {invites.map((invite, i) => {
      const compAvailable = !!(props.compMemberships && props.compMemberships > 0); 
      if (detailSet.has(i)) {
        return <InviteDetailComponent key={invite._id} index={i} invite={invite}
          domain={props.domain}
          compAvailable={compAvailable}
          getGroup={getGroup}
          getGroups={getGroups}
          searchGroups={searchGroups}
          resendInvite={resendInvite}
          deleteInvite={deleteInvite}
          onFinishDetail={finishDetailAtIndex} />;
      }
      return <InviteListItem key={invite._id} index={i} invite={invite} now={now}
        doShowDetail={showDetailAtIndex} displayTZ={props.displayTZ} />;
    })}
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") }/>
  </div>;
}

interface InviteProps {
  now: DateTime,
  displayTZ: string,
  index: number,
  invite: InviteWithURL,
  doShowDetail(index: number): void
}

function InviteListItem(props: InviteProps): React.ReactElement<any, any> {
  const { invite } = props;
  const expired = props.now > invite.validBefore;
  if (props.displayTZ) {
    invite.validBefore.setZone(props.displayTZ);
  }
  return <article className={"account-invite-list-item" + (expired ? " expired" : "")}>
    <header>
      <div>
        {invite.invitetype === InviteType.mass ? invite.campaignName : invite.email}
      </div>
      <div className="right-controls">
        <span className="expiration">{invite.validBefore.toFormat("LL/dd/yyyy h:mm a")}</span>
        <IconButton data-testid="show-invite-details" 
          onClick={() => props.doShowDetail(props.index)}><KeyboardArrowDownIcon /></IconButton>
      </div>
    </header>
  </article>
}

interface InviteDetailProps {
  index: number,
  invite: InviteWithURL,
  domain: string,
  compAvailable: boolean,
  getGroup(groupID: string): Promise<Group | null>,
  getGroups(groupIDs: Array<string>): Promise<Array<Group>>,
  searchGroups(search: string): Promise<Array<Group>>,
  resendInvite(invite: InviteRequest, index: number): Promise<InviteResponse>,
  deleteInvite(index: number): Promise<void>,
  onFinishDetail(index: number): void
}

function InviteDetailComponent(props: InviteDetailProps): React.ReactElement<any, any> {
  const isGlyph = props.domain.indexOf("glyphapp") >= 0;
  const { invite } = props;
  const [errMsg, setErrMsg] = React.useState<string>("");
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [sendEmail, setSendEmail] = React.useState<boolean>(false);
  const [complementary, setComplementary] = React.useState<boolean>(false);
  const [inviteUrl, setInviteUrl] = React.useState<string>(invite.tokenUrl || "");
  const [groupSearchValue, setGroupSearchValue] = React.useState<string>("");
  const [huddleSearchValue, setHuddleSearchValue] = React.useState<string>("");
  const [groups, setGroups] = React.useState<Array<Group>>([]);
  const [huddleGroups, setHuddleGroups] = React.useState<Array<Group>>([]);
  const [selectedGroups, setSelectedGroups] = React.useState<Array<Group>>([]);
  const [huddle, setHuddle] = React.useState<Group | null>(null);
  const [groupIDs, setGroupIDs] = React.useState<Array<string>>(invite.groupIDs || []);
  const qrCodeRef = React.useRef(null);
  const linkInputRef = React.useRef<HTMLInputElement>(null);
  const [startDate, setStartDate] = React.useState<DateTime>(invite.validAfter || DateTime.now().startOf("day"));
  const [endDate, setEndDate] = React.useState<DateTime>(invite.validBefore || DateTime.now().plus({days: 7}).endOf("day"));
  const [startDateErr, setStartDateErr] = React.useState<string>("");
  const [endDateErr, setEndDateErr] = React.useState<string>("");
  const [copyText, setCopyText] = React.useState<string>("Copy To Clipboard");
  const [groupSearchTimer, setGroupSearchTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [huddleSearchTimer, setHuddleSearchTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [qrCode, ] = React.useState<QRCode>(new QRCode({
    width: 350,
    height: 350,
    margin: 5,
    image: isGlyph ? "/img/glyph-icon-180x180.png" : "/img/workspace650.png",
    imageOptions: {
      margin: 20
    },
    backgroundOptions: {
      color: "#FFFFFF00"
    },
    cornersSquareOptions: {
      type: "extra-rounded"
    },
    dotsOptions: {
      color: isGlyph ? "#0070C0" : "#db38a6"
      /*gradient: {
        type: "linear",
        colorStops: [{offset: 0, color: "#f24341"}, {offset: 1, color: "#db38a6"}]
      }*/
    }
  }));

  React.useEffect(() => {
    console.log("InviteDetailComponent mounted");
    setComplementary(!!invite.compIfPossible);
    props.searchGroups("").then(async (groups) => {
      setGroups(groups);
      setHuddleGroups(groups);
      if (invite.groupIDs) {
        setSelectedGroups(await props.getGroups(invite.groupIDs as Array<string>));
      }
      if (invite.huddleGroupID) {          
        setHuddle(await props.getGroup(invite.huddleGroupID));
      }
    });
  }, []);

  React.useEffect(() => {
    if (qrCodeRef.current) {
      qrCode.append(qrCodeRef.current);
    }
  }, [qrCodeRef.current])

  React.useEffect(() => {
    if (inviteUrl) {
      qrCode.update({data: inviteUrl});
      if (linkInputRef.current) {
        linkInputRef.current.select();
      }
    }
  }, [inviteUrl]);

  React.useEffect(function () {
    const searchVal = groupSearchValue.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    
    groupSearchTimer && clearTimeout(groupSearchTimer);
    setGroupSearchTimer(setTimeout(() => {
      props.searchGroups(searchVal).then(groups => {
        setGroups(groups);
      });
    }, 1000));
  }, [groupSearchValue]);

  React.useEffect(function () {
    const searchVal = huddleSearchValue.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    
    huddleSearchTimer && clearTimeout(huddleSearchTimer);
    setHuddleSearchTimer(setTimeout(() => {
      props.searchGroups(searchVal).then(groups => {
        setHuddleGroups(groups);
      });
    }, 1000));
  }, [huddleSearchValue]);

  const isMassOnboardCampaign = invite.invitetype === InviteType.mass;

  async function doInvite() {
    try {
      const req = invite.invitetype === InviteType.mass ? {
        _id: invite._id,
        client_id: secrets.SC_CLIENT_ID,
        invitetype: InviteType.mass,
        email: "",
        membertype: MemberType.paid,
        role: invite.memberrole,
        sendEmail: false,
        campaignName: invite.campaignName,
        groupIDs: groupIDs || [],
        huddleGroupID: huddle?._id,
        validAfter: startDate,
        validBefore: endDate,
        compIfPossible: complementary
      } : {
        client_id: secrets.SC_CLIENT_ID,
        invitetype: InviteType.individual,
        email: invite.email,
        role: invite.memberrole,
        membertype: MemberType.paid,
        sendEmail: sendEmail,
        groupIDs: groupIDs || [],
        huddleGroupID: huddle?._id,
        compIfPossible: complementary
      };

      const tokenData = await props.resendInvite(req, props.index);
      invite.tokenUrl = tokenData.url;
      setInviteUrl(tokenData.url);
    } catch (err) {
      console.error("Error sending invite:", err);
      setErrMsg((err as Error).message);
    }
  }
  async function deleteInvite() {
    try {
      if (deleting) { return; }
      setDeleting(true);
      await props.deleteInvite(props.index);
    } catch (err) {
      console.error("Error deleting member:", err);
      setErrMsg((err as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "startDate") {
      if (!startDate || !startDate.isValid) {
        setStartDateErr("Please, set a starting date/time");
      } else if (endDate && endDate < startDate) {
        setStartDateErr("Starting date/time is after the ending date/time");
      } else {
        setStartDateErr("");
      }
    } else if (propName === "endDate") {
      if (!endDate || !endDate.isValid) {
        setEndDateErr("Please, set an ending date/time");
      } else if (startDate && endDate < startDate) {
        setEndDateErr("Ending date/time is before the starting date/time");
      } else {
        setEndDateErr("");
      }
    }
  }
  function onDateError(error: DateTimeValidationError | null, propName: string) {
    if (propName === "startDate") {
      switch (error) {
        case "maxDate":
          setStartDateErr("Starting date/time must be in the past");
          break;
        case null:
          setStartDateErr("");
          break;
        default:
          setStartDateErr("Please, set a starting date/time");
          break;
      }
    } else if (propName === "endDate") {
      switch (error) {
        case "maxDate":
          setEndDateErr("Ending date/time must be in the past");
          break;
        case null:
          setEndDateErr("");
          break;
        default:
          setEndDateErr("Please, set an ending date/time");
          break;
      }
    }
  }

  function selectedGroup(_event: React.SyntheticEvent, values: readonly Group[] | null) {
    if (values) {
      setGroupIDs(values.map(g => g._id));
      setSelectedGroups(Array.from(values));
    } else {
      setGroupIDs([]);
      setSelectedGroups([]);
    }
  }

  function selectedHuddle(_event: React.SyntheticEvent, value: Group | null) {
    setHuddle(value);
    if (!value) { return; } 
    if (!groupIDs.includes(value._id)) {
      setGroupIDs([...groupIDs, value._id]);
    }
    if (!groups.find(group => group._id === value._id)) {
      setGroups([...groups, value]);
    }
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyText("Copied!")
    } catch (err) {
      console.error("Error copying to clipboard:", err);
      setErrMsg("Couldn't copy to clipboard");
    }
  }

  function renderIndividual(): React.ReactElement<any, any> {
    return <>
      <div className="form-row-narrow">
        <FormControl fullWidth>
          <TextField id="email-input" label="Email" size="small" type="email" name="email" required
            value={props.invite.email} fullWidth variant="outlined"
            disabled={true} />
        </FormControl>
      </div>
      <div className="form-row">
        <FormControlLabel control={
          <Checkbox color="secondary"
              checked={sendEmail}
              onChange={event => setSendEmail(event.target.checked)}
              inputProps={{ "aria-label": "primary checkbox" }}
            />}
          label="Send Link in Email Invitation"
          name="sendEmail" />
      </div>
    </>;
  }

  function renderMassOnboarding(): React.ReactElement<any, any> {
    return <>
      <div className="form-row">
        <FormControl fullWidth>
          <TextField id="campaign-input" label="Campaign Name" size="small"
            type="text" name="campaignName"
            disabled={true}
            value={invite.campaignName} fullWidth variant="outlined" />
        </FormControl>
      </div>
      <div className="form-row">
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateTimePicker
            label="Valid After"
            name="startDate"
            value={startDate}
            onChange={(newValue) => {
              newValue && setStartDate(newValue);
            }}
            onError={e => onDateError(e, "startDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: startDateErr,
                error: !!startDateErr,
                onBlur: onBlur
              }
            }}
          />
          <DateTimePicker
            label="Expiration"
            name="endDate"
            value={endDate}
            onChange={(newValue) => {
              newValue && setEndDate(newValue);
            }}
            onError={e => onDateError(e, "endDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: endDateErr,
                error: !!endDateErr,
                onBlur: onBlur
              }
            }}
          />
        </LocalizationProvider>
      </div>
    </>;
  }

  return <article className={"account-invite-list-detail-item" + (inviteUrl ? " withTokenURL" : "")}>
    { errMsg && <Alert
        variant="filled"
        severity="error" onClick={() => setErrMsg("")}>{errMsg}</Alert> }
    <div className="form-wrapper">
      {isMassOnboardCampaign ?
        renderMassOnboarding() :
        renderIndividual()}
      <div className="group-search-container form-row">
        <Autocomplete id="group-search"
          multiple
          fullWidth
          size="small"
          options={groups}
          getOptionLabel={(group: Group) => group.groupname}
          value={selectedGroups}
          onChange={selectedGroup}

          onInputChange={(_event, newInputValue) => {
            setGroupSearchValue(newInputValue);
          }}
          isOptionEqualToValue={(option, value) => option._id === value?._id}
          renderInput={(params) =>
            <TextField variant="outlined" {...params} label="Add To Groups:" />} />
      </div>
      <div className="group-search-container form-row-med">
        <Autocomplete id="huddle-search"
          fullWidth
          size="small"
          options={huddleGroups}
          getOptionLabel={(group: Group) => group.groupname}
          value={huddle}
          onInputChange={(_event, newInputValue) => {
            setHuddleSearchValue(newInputValue);
          }}
          onChange={selectedHuddle}
          isOptionEqualToValue={(option, value) => option._id === value?._id}
          renderInput={params =>
            <TextField variant="outlined"
              color="secondary" {...params}
              helperText="Invitees will be directed to start/join a huddle in this group after onboarding."
              label="Huddle Group (Optional):" placeholder="Group Name" />}
        />
      </div>
      {props.compAvailable ?  
        <div> 
          <FormControlLabel control={
            <Checkbox color="secondary"
                checked={complementary}
                onChange={event => setComplementary(event.target.checked)}
                inputProps={{ "aria-label": "primary checkbox" }}
              />}
            label="Allocate Complementary Membership if Available"
            name="complementary" />
          <Tooltip className="info-tooltip" title={
            `Allocates a complementary membership if one is available when the invite is claimed. 
              If none are available, the membership will be created as normal.`} arrow>
            <InfoIcon className="info" />
          </Tooltip>
        </div> : <></>}
      <div className="token-url-container">
        <div className="form-row-narrow">
          <FormControl fullWidth>
            <TextField id="code-display" size="small" type="email" name="tokenlink"
              value={inviteUrl} fullWidth variant="outlined" autoFocus={true}
              inputRef={linkInputRef} />
          </FormControl>
        </div>
        <Button color="primary" variant="contained"
            disableRipple disableTouchRipple
            onClick={copyToClipboard}>{copyText}</Button>
        <div id="qrcode-viewer" ref={qrCodeRef} />
      </div>
      <div className="button-row">
        <Button onClick={doInvite} disableRipple disableTouchRipple>
          {isMassOnboardCampaign ? "Save" : "Resend"}
        </Button>
        {deleting ?
          <CircularProgress size={20} color="secondary" /> :
          <Button variant="contained" color="secondary" className="delete-btn"
              disableRipple disableTouchRipple
              onClick={deleteInvite}>
            Delete
          </Button>}
        <Button variant="contained" color="primary"
            disableRipple disableTouchRipple
            onClick={() => props.onFinishDetail(props.index)}>
          Done
        </Button>
      </div>
    </div>
  </article>;
}
